// Meeting create/edit form — extracted from meetings.tsx so it can be reused
// by the unified ErfassenPanel (Kalender) and the Meetings page. Mirrors the
// pattern used for EntryFormDialog / AbsenceFormDialog: `useMeetingForm` is
// the headless hook returning {title, body, footer, isDirty}; the standalone
// `MeetingFormDialog` wraps it in a SidePanel.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link2, MapPin } from 'lucide-react'
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react'
import { api, type Meeting } from '../lib/api'
import { invalidateMeetingData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { SidePanel } from './ui/side-panel'

export interface MeetingFormBodyResult {
  title: string
  description: string
  body: ReactNode
  footer: ReactNode
  isDirty: boolean
}

interface UseMeetingFormArgs {
  open: boolean
  myId: string
  meeting?: Meeting | null
  defaultDate?: string
  onClose: () => void
  onSaved?: () => void
}

function defaultStart(base?: Date): string {
  const d = base ? new Date(base) : new Date()
  d.setMinutes(0, 0, 0)
  d.setHours(base ? 9 : d.getHours() + 1)
  return toLocalInput(d.toISOString())
}

function defaultEnd(base?: Date): string {
  const d = base ? new Date(base) : new Date()
  d.setMinutes(0, 0, 0)
  d.setHours(base ? 10 : d.getHours() + 2)
  return toLocalInput(d.toISOString())
}

function toLocalInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function useMeetingForm({
  open,
  myId,
  meeting,
  defaultDate,
  onClose,
  onSaved,
}: UseMeetingFormArgs): MeetingFormBodyResult {
  const isEdit = !!meeting
  const queryClient = useQueryClient()
  const baseDate = defaultDate ? new Date(defaultDate) : undefined

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState(() =>
    meeting ? toLocalInput(meeting.startTime) : defaultStart(baseDate),
  )
  const [endDate, setEndDate] = useState(() =>
    meeting ? toLocalInput(meeting.endTime) : defaultEnd(baseDate),
  )
  const [onlineUrl, setOnlineUrl] = useState('')
  const [location, setLocation] = useState('')
  const [participants, setParticipants] = useState<Set<string>>(new Set())
  const [countsAsWork, setCountsAsWork] = useState(false)
  const [search, setSearch] = useState('')
  const [dirty, setDirty] = useState(false)

  // Reset when reopened — mirror behaviour of the other Erfassen tabs.
  useEffect(() => {
    if (!open) return
    if (meeting) {
      setTitle(meeting.title)
      setDescription(meeting.description ?? '')
      setStartDate(toLocalInput(meeting.startTime))
      setEndDate(toLocalInput(meeting.endTime))
      setOnlineUrl(meeting.onlineUrl ?? '')
      setLocation(meeting.location ?? '')
      setParticipants(
        new Set(meeting.participants.filter((p) => p.userId !== myId).map((p) => p.userId)),
      )
      setCountsAsWork(meeting.countsAsWork)
    } else {
      const b = defaultDate ? new Date(defaultDate) : undefined
      setTitle('')
      setDescription('')
      setStartDate(defaultStart(b))
      setEndDate(defaultEnd(b))
      setOnlineUrl('')
      setLocation('')
      setParticipants(new Set())
      setCountsAsWork(false)
    }
    setSearch('')
    setDirty(false)
  }, [open, meeting, myId, defaultDate])

  const markDirty = () => setDirty(true)

  // Default für NEUE Meetings aus dem Admin-Setting (async — der Reset-Effect
  // oben läuft synchron beim Öffnen; ohne warmen Cache käme der Prefill sonst
  // zu spät). Nur setzen solange der User nichts angefasst hat.
  const { data: rulesData } = useQuery({
    queryKey: ['vacation-rules'],
    queryFn: () => api.getVacationRules(),
    enabled: open && !meeting,
  })
  const countMeetingsDefault = (rulesData?.rules as { countMeetingsInActual?: boolean } | undefined)
    ?.countMeetingsInActual
  useEffect(() => {
    if (!open || meeting || dirty) return
    if (typeof countMeetingsDefault === 'boolean') setCountsAsWork(countMeetingsDefault)
  }, [open, meeting, dirty, countMeetingsDefault])

  const { data: usersData } = useQuery({
    queryKey: ['documents', 'sendable-users'],
    queryFn: () => api.documentSendableUsers(),
    enabled: open,
  })
  const users = (usersData?.users ?? []).filter((u) => u.id !== myId)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    )
  }, [users, search])

  const save = useMutation({
    mutationFn: async () => {
      const startIso = new Date(startDate).toISOString()
      const endIso = new Date(endDate).toISOString()
      if (isEdit && meeting) {
        await api.updateMeeting(meeting.id, {
          title,
          description: description || null,
          startTime: startIso,
          endTime: endIso,
          onlineUrl: onlineUrl || null,
          location: location || null,
          countsAsWork,
        })
        // Diff participants — add/remove against existing
        const wanted = new Set(participants)
        const currentOthers = new Set(
          meeting.participants.filter((p) => p.userId !== myId).map((p) => p.userId),
        )
        const toAdd = [...wanted].filter((id) => !currentOthers.has(id))
        const toRemove = [...currentOthers].filter((id) => !wanted.has(id))
        await Promise.all([
          ...toAdd.map((id) => api.addMeetingParticipant(meeting.id, id)),
          ...toRemove.map((id) => api.removeMeetingParticipant(meeting.id, id)),
        ])
      } else {
        await api.createMeeting({
          title,
          description: description || null,
          startTime: startIso,
          endTime: endIso,
          onlineUrl: onlineUrl || null,
          location: location || null,
          countsAsWork,
          participantUserIds: [...participants],
        })
      }
    },
    onSuccess: () => {
      // Selbst invalidieren (wie useEntryForm) — der Kalender mountete das
      // Panel ohne onSaved, wodurch ein neues Meeting bis zum Reload
      // unsichtbar blieb (Audit-Finding, critical).
      invalidateMeetingData(queryClient)
      notify.success(isEdit ? 'Meeting aktualisiert' : 'Meeting angelegt')
      onSaved?.()
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    save.mutate()
  }

  const toggle = (id: string) => {
    setParticipants((p) => {
      const n = new Set(p)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
    markDirty()
  }

  const body = (
    <form id="meeting-form" onSubmit={onSubmit} className="space-y-4 pb-2">
      <div className="space-y-2">
        <Label htmlFor="m-title">Titel</Label>
        <Input
          id="m-title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value.slice(0, 200))
            markDirty()
          }}
          placeholder="z.B. Sprint-Review KW 19"
          autoFocus
        />
      </div>

      {/* Start/Ende nebeneinander wie im Eintrag-Tab (C5) */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="m-start">Start</Label>
          <Input
            id="m-start"
            type="datetime-local"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value)
              markDirty()
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="m-end">Ende</Label>
          <Input
            id="m-end"
            type="datetime-local"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value)
              markDirty()
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="m-url">
            <span className="inline-flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5" /> Online-Link
            </span>
          </Label>
          <Input
            id="m-url"
            type="url"
            inputMode="url"
            value={onlineUrl}
            onChange={(e) => {
              setOnlineUrl(e.target.value)
              markDirty()
            }}
            placeholder="https://meet.it.financeflow.de/…"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="m-loc">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> Ort (optional)
            </span>
          </Label>
          <Input
            id="m-loc"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value)
              markDirty()
            }}
            placeholder="z.B. Besprechungsraum 2"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="m-desc">Beschreibung (optional)</Label>
        <textarea
          id="m-desc"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value.slice(0, 4000))
            markDirty()
          }}
          rows={3}
          placeholder="Agenda, Vorbereitung, Materialien …"
          className="flex w-full rounded-md border border-border-default bg-bg-overlay px-3 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30 resize-y"
        />
      </div>

      <label className="flex items-start gap-2.5 rounded-lg border border-border-subtle bg-bg-overlay/40 p-3 cursor-pointer">
        <input
          type="checkbox"
          checked={countsAsWork}
          onChange={(e) => {
            setCountsAsWork(e.target.checked)
            markDirty()
          }}
          className="mt-0.5 h-4 w-4 accent-[var(--accent,#e8590c)]"
        />
        <span className="text-sm">
          <span className="text-text-primary">Zählt als Arbeitszeit (Ist-Zeit)</span>
          <span className="block text-xs text-text-tertiary">
            Gilt für alle Teilnehmer — die Meeting-Zeit fließt in Stundenkonto und Reports ein.
          </span>
        </span>
      </label>

      <div className="space-y-2">
        <Label htmlFor="m-search">Teilnehmer ({participants.size})</Label>
        <Input
          id="m-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suchen…"
        />
        <div className="max-h-56 overflow-y-auto rounded-lg border border-border-subtle divide-y divide-border-subtle">
          {filtered.length === 0 ? (
            <p className="p-3 text-sm text-text-tertiary">Keine Treffer.</p>
          ) : (
            filtered.map((u) => {
              const checked = participants.has(u.id)
              return (
                <label
                  key={u.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-bg-overlay/40 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(u.id)}
                    className="accent-accent h-4 w-4"
                  />
                  <span className="text-sm text-text-primary flex-1 min-w-0 truncate">
                    {u.name}
                  </span>
                  <span className="text-xs text-text-tertiary shrink-0">{u.email}</span>
                </label>
              )
            })
          )}
        </div>
      </div>
    </form>
  )

  const footer = (
    <>
      <Button variant="ghost" type="button" onClick={onClose}>
        Abbrechen
      </Button>
      <Button type="submit" form="meeting-form" disabled={!title.trim() || save.isPending}>
        {isEdit ? 'Speichern' : 'Anlegen'}
      </Button>
    </>
  )

  return {
    title: isEdit ? 'Meeting bearbeiten' : 'Neues Meeting',
    description: isEdit
      ? 'Termin, Link und Teilnehmer anpassen.'
      : 'Plane einen Online-Termin und lade Kolleg:innen ein.',
    body,
    footer,
    isDirty: dirty,
  }
}

/** Render-prop wrapper for `useMeetingForm`. */
export function MeetingFormBody(props: {
  open: boolean
  myId: string
  meeting?: Meeting | null
  defaultDate?: string
  onClose: () => void
  onSaved?: () => void
  children: (result: MeetingFormBodyResult) => ReactNode
}) {
  const { children, ...rest } = props
  const result = useMeetingForm(rest)
  return <>{children(result)}</>
}

/** Standalone SidePanel-wrapped MeetingDialog. Kept for callers that just
 * want a meeting form without the Erfassen tabs. */
export function MeetingFormDialog({
  open,
  myId,
  meeting,
  defaultDate,
  onClose,
  onSaved,
}: {
  open: boolean
  myId: string
  meeting?: Meeting | null
  defaultDate?: string
  onClose: () => void
  onSaved?: () => void
}) {
  const { title, description, body, footer } = useMeetingForm({
    open,
    myId,
    meeting,
    defaultDate,
    onClose,
    onSaved,
  })
  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={footer}
    >
      {body}
    </SidePanel>
  )
}
